import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

interface ReviewRequest {
  id: string;
  account_id: string;
  order_id: string;
  marketplace_id: string | null;
  asin: string | null;
  sku: string | null;
  product_title: string | null;
  buyer_name: string | null;
  order_date: string | null;
  delivery_date: string | null;
  request_type: string;
  status: string;
  scheduled_for: string | null;
  sent_at: string | null;
  error_message: string | null;
  review_received: number;
  review_rating: number | null;
  created_at: string;
}

interface Stats {
  threadsByStatus: Array<{ status: string; count: number }>;
  reviewsByStatus: Array<{ status: string; count: number }>;
  avgResponseHours: number | null;
}

const PAGE_SIZE = 50;

const STATUS_COLORS: Record<string, string> = {
  eligible: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  responded: 'bg-purple-100 text-purple-800',
  skipped: 'bg-gray-100 text-gray-600',
};

const MARKETPLACE_FLAGS: Record<string, string> = {
  ATVPDKIKX0DER: 'US',
  A2EUQ1WTGCTBG2: 'CA',
  A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK',
  A1PA6795UKMFR9: 'DE',
  A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT',
  A1RKKUPIHCS9HS: 'ES',
  A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
  A1C3SOZF4HXIYN: 'PL',
  A1S75YUIWTBMNN: 'BE',
};

export function ReviewRequests() {
  const [reviews, setReviews] = useState<ReviewRequest[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const { displayName, parentNickname } = useNicknames();

  useEffect(() => {
    setPage(0);
    loadData(0);
  }, [statusFilter]);

  async function loadData(p: number) {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('limit', String(PAGE_SIZE + 1)); // +1 to detect hasNext
    params.set('offset', String(p * PAGE_SIZE));

    const [reviewsData, statsData] = await Promise.all([
      api.get<ReviewRequest[]>(`/messages/reviews?${params}`),
      api.get<Stats>('/messages/stats'),
    ]);
    setReviews(reviewsData);
    setStats(statsData);
    setLoading(false);
  }

  function goPage(p: number) {
    setPage(p);
    loadData(p);
  }

  async function sendNow(id: string) {
    setSendingId(id);
    try {
      await api.post(`/messages/reviews/${id}/send`);
      await loadData(page);
    } catch (err) {
      alert(`Send failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSendingId(null);
    }
  }

  async function bulkAction(action: 'retry-failed' | 'send-eligible') {
    setActionLoading(true);
    try {
      await api.post(`/messages/reviews/bulk/${action}`);
      await loadData(page);
    } finally {
      setActionLoading(false);
    }
  }

  const hasNext = reviews.length > PAGE_SIZE;
  const displayReviews = hasNext ? reviews.slice(0, PAGE_SIZE) : reviews;

  const totalSent = stats?.reviewsByStatus?.find(r => r.status === 'sent')?.count ?? 0;
  const totalResponded = stats?.reviewsByStatus?.find(r => r.status === 'responded')?.count ?? 0;
  const totalFailed = stats?.reviewsByStatus?.find(r => r.status === 'failed')?.count ?? 0;
  const totalScheduled = stats?.reviewsByStatus?.find(r => r.status === 'scheduled')?.count ?? 0;
  const responseRate = totalSent > 0 ? ((totalResponded / totalSent) * 100).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <SummaryCard label="Total Sent" value={String(totalSent)} />
        <SummaryCard label="Response Rate" value={`${responseRate}%`} />
        <SummaryCard label="Pending" value={String(totalScheduled)} />
        <SummaryCard label="Failed" value={String(totalFailed)} color={totalFailed > 0 ? 'text-red-600' : undefined} />
      </div>

      {/* Filters + Bulk Actions */}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
        >
          <option value="">All Status</option>
          <option value="eligible">Eligible</option>
          <option value="scheduled">Scheduled</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="responded">Responded</option>
          <option value="skipped">Skipped</option>
        </select>

        <div className="ml-auto flex gap-2">
          {totalFailed > 0 && (
            <button
              onClick={() => bulkAction('retry-failed')}
              disabled={actionLoading}
              className="rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 text-xs font-medium text-orange-700 hover:bg-orange-100 disabled:opacity-50"
            >
              Retry All Failed ({totalFailed})
            </button>
          )}
          {totalScheduled > 0 && (
            <button
              onClick={() => bulkAction('send-eligible')}
              disabled={actionLoading}
              className="rounded-lg border border-brand-300 bg-brand-50 px-3 py-2 text-xs font-medium text-brand-700 hover:bg-brand-100 disabled:opacity-50"
            >
              Send All Scheduled ({totalScheduled})
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Order</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Product</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Mkt</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Scheduled</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Sent</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : displayReviews.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">
                  No review requests yet
                </td>
              </tr>
            ) : (
              displayReviews.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-mono">{r.order_id}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="max-w-[200px]" title={r.product_title ?? r.asin ?? ''}>
                      {r.sku && parentNickname(r.sku) && (
                        <span className="text-[10px] text-gray-400 block">{parentNickname(r.sku)}</span>
                      )}
                      <span className="truncate block">{r.sku ? displayName(r.sku) : (r.product_title ?? r.asin ?? 'N/A')}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {r.marketplace_id ? (MARKETPLACE_FLAGS[r.marketplace_id] ?? r.marketplace_id) : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[r.status] ?? 'bg-gray-100'}`}>
                      {r.status}
                    </span>
                    {r.error_message && (
                      <p className="mt-1 text-[10px] text-gray-500" title={r.error_message}>
                        {friendlyError(r.error_message)}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {r.scheduled_for ? new Date(r.scheduled_for).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {r.sent_at ? new Date(r.sent_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {(r.status === 'eligible' || r.status === 'scheduled' || r.status === 'failed') && (
                      <button
                        onClick={() => sendNow(r.id)}
                        disabled={sendingId === r.id}
                        className="rounded bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                      >
                        {sendingId === r.id ? 'Sending...' : r.status === 'failed' ? 'Retry' : 'Send Now'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && (page > 0 || hasNext) && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => goPage(page - 1)}
            disabled={page === 0}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">Page {page + 1}</span>
          <button
            onClick={() => goPage(page + 1)}
            disabled={!hasNext}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function friendlyError(msg: string): string {
  if (msg.includes('already requested')) return 'Already requested';
  if (msg.includes('outside the 5-30 day range')) return 'Outside 5-30 day window';
  if (msg.includes('multi-channel fulfillment') || msg.includes('Multi-channel')) return 'MCF order (not eligible)';
  if (msg.includes('exceeded your quota')) return 'Rate limited (will retry)';
  if (msg.includes('Account not found')) return 'Account not found';
  // Strip SP-API prefix for anything else
  return msg.replace(/^\[retry\]*:?\s*/g, '').replace(/^SP-API Solicitation error:\s*/i, '');
}

function SummaryCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${color ?? ''}`}>{value}</p>
    </div>
  );
}
